import { Text, Tooltip } from '@mantine/core';
import { TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { memo, type FC } from 'react';

import type { HealthRow } from './grouping';

/**
 * Below this, a move is noise. Sales arrive in daily sync batches and a run rate is a ratio of
 * two estimates, so a 2% wobble between adjacent windows says nothing about demand — printing
 * an arrow for it would train the operator to ignore all of them.
 */
const MATERIALITY_THRESHOLD_PCT = 5;

type Props = {
    row: HealthRow;
    /** The window being viewed, so the tooltip can say what is being compared with what. */
    windowDays: number;
    /**
     * What the CURRENT figure is. The prior side is past sales either way, so on FORWARD this
     * is a forecast measured against actuals rather than one window of history against the
     * one before it — a real difference in meaning, and the tooltip has to say which.
     */
    basis: 'FORWARD' | 'BACKWARD';
};

/**
 * The DRR trend cue: how this SKU's run rate moved against the previous window of the SAME
 * length.
 *
 * The reason it is on this screen at all is that reorder points are now sized on a window the
 * brand picks per SKU. That is a decision somebody has to revisit — and the signal that it needs
 * revisiting is precisely a run rate that has moved consistently. An arrow beside the rate is
 * what turns "here is a number" into "here is a number you should look at again".
 *
 * Rendered as a cue, not a metric: no arrow below the materiality threshold, and never a
 * fabricated figure where none exists.
 */
const DrrDelta: FC<Props> = ({ row, windowDays, basis }) => {
    const against =
        basis === 'FORWARD'
            ? `the last ${windowDays} days of actual sales`
            : `the previous ${windowDays} days`;
    // A SKU that sold nothing in the prior window and something now. Real information, and the
    // one case where a percentage genuinely cannot be computed — "new" says it outright rather
    // than showing an infinity or, worse, a dash that reads as "no change".
    if (row.drrDeltaPct == null) {
        if (row.drrPrevious == null && row.drr > 0) {
            return (
                <Tooltip label={`No sales in the last ${windowDays} days — nothing to compare against`}>
                    <Text size="10px" c="dimmed">
                        new
                    </Text>
                </Tooltip>
            );
        }
        return null;
    }

    if (Math.abs(row.drrDeltaPct) < MATERIALITY_THRESHOLD_PCT) {
        return (
            <Tooltip
                label={`Flat — ${row.drrDeltaPct > 0 ? '+' : ''}${row.drrDeltaPct}% vs ${against} (${row.drrPrevious}/day)`}
            >
                <Text size="10px" c="dimmed">
                    ·
                </Text>
            </Tooltip>
        );
    }

    const isUp = row.drrDeltaPct > 0;
    return (
        <Tooltip
            label={`${isUp ? 'Up' : 'Down'} ${Math.abs(row.drrDeltaPct)}% vs ${against} (${row.drrPrevious}/day). Rising demand on a long reorder window means the threshold is sized on stale volume.`}
        >
            {/* Colour is NOT good/bad — rising demand is not a problem and falling demand is
                not a win. It only distinguishes direction, which is why both use the same
                weight and neither uses red. */}
            <Text
                size="10px"
                c={isUp ? 'teal.7' : 'orange.7'}
                component="span"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
            >
                {isUp ? <TrendUpIcon size={11} /> : <TrendDownIcon size={11} />}
                {Math.abs(row.drrDeltaPct)}%
            </Text>
        </Tooltip>
    );
};

export default memo(DrrDelta);
